import { useEffect } from 'react';
import Helmet from 'react-helmet';
import { useSelector } from 'react-redux';
import { push } from 'redux-first-history';

import { useRequest } from 'ahooks';

import { Heading, Page } from '@zillow/constellation-10';
import { logError } from '@zg-rentals/log-error';

import { getMarketTrends } from 'app/shared/flux/actions/AreaActions';
import { useAppDispatch } from 'app/shared/flux/hooks';
import pathsManifest from 'app/shared/utils/pathsManifest';
import { decorate } from 'app/shared/models/TopMarkets';
import StringConstants from 'app/shared/constants/StringConstants';
import { emitAreaSearchEvent, emitMarketTrendsStartPageViewEvent } from 'app/shared/constants/MarketTrendsCSEvents';

import SearchMarkets from 'app/shared/components/SearchMarkets';
import TopMarkets from 'app/shared/components/TopMarkets';
import UpsellCarouselModule from 'app/shared/modules/UpsellCarouselModule';

import type { RegionSuggestionsDto } from '@zg-rentals/ts-rental-growth-api-client';
import type { State } from 'app/shared/types/State';

const MarketTrendsPage = () => {
  const dispatch = useAppDispatch();
  const user = useSelector((state: State) => state.user);
  const topMarkets = useSelector((state: State) => state.topMarkets);
  const pathname = useSelector((state: State) => state.router.location.pathname);
  const queryString = useSelector((state: State) => state.router.location.search);

  useEffect(() => {
    emitMarketTrendsStartPageViewEvent(user);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    run: submit,
    loading: isLoading,
    error: submitError,
  } = useRequest(
    async ({ resourceId }: RegionSuggestionsDto) => {
      if (!resourceId) {
        return;
      }
      await dispatch(getMarketTrends({ resourceId }));
      emitAreaSearchEvent(user);
      dispatch(push(`${pathsManifest.MarketTrendsPage}/${resourceId}/`));
    },
    {
      manual: true,
      onError: (err) => {
        logError({
          error: err,
          errorType: 'MarketTrends',
          context: {
            details: 'Market Stats Page: Error fetching Market Stats (CSR)',
            root: 'pages/MarketTrendsPage#submit',
          },
        });
      },
    },
  );
  const error = submitError ? 'An error occurred. Try again.' : null;

  const topRentalMarkets = topMarkets.topRentalMarkets.map((market) => decorate(market, queryString));

  return (
    <>
      <Helmet
        meta={[
          {
            name: 'description',
            content:
              'Use Zillow Rental Manager to compare rent across locations and see current and historical rental market data compiled by the leading real estate marketplace.',
          },
        ]}
        title="Rent Comparison Tool & Rental Market Trends Data | Zillow Rental Manager"
        link={[{ rel: 'canonical', href: `${StringConstants.ZILLOW_URL}${pathname}` }]}
      />
      <Page.Root css={{ backgroundColor: 'bg.screen.neutral' }}>
        <Page.Content>
          <Heading level={1} textStyle="responsive.heading-lg" fontFamily="heading-alt" css={{ marginTop: '600' }}>
            Rental Market Trends
          </Heading>
          <SearchMarkets submit={submit} isLoading={isLoading} error={error} />
          <TopMarkets markets={topRentalMarkets} />
          <UpsellCarouselModule addMarginTop={true} />
        </Page.Content>
      </Page.Root>
    </>
  );
};

export default MarketTrendsPage;
